import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Menu, MenuItem, Avatar, Box, Select, FormControl, InputLabel, FormControlLabel, Switch, Divider, Button } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import travelMindsLogo from '../assets/travelminds-logo-h.webp';
import { useUser } from '../contexts/UserContext';
import { SelectChangeEvent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getRoleDisplayName } from '../utils/roleUtils';

interface Client {
  id: number;
  agency_name: string;
}

interface TopBarProps {
  onLogout: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onLogout }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const auth = useUser();
  const { user, selectedClientId, setSelectedClientId, isAdmin, isCognitoAdmin, clients, handleViewModeChange, viewMode } = auth;
  const clientAssignments = user?.client_assignments ?? [];
  const userEmail = user?.email ?? '';
  const userDisplayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || userEmail;
  const userInitial = (userDisplayName || userEmail).charAt(0).toUpperCase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const location = useLocation();

  // Check if we're on a connect-instances route (should use sessionStorage)
  const isConnectInstancesRoute = location.pathname.includes('/admin/connect-instances');

  // Show agency selector only when in admin mode
  const showAgencySelector = isAdmin();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    setIsLoggingOut(true);
    onLogout();
  };

  const handleClientChange = (event: SelectChangeEvent<number>) => {
    const newClientId = Number(event.target.value);
    console.log('[TopBar] Changing selected client to:', newClientId);
    setSelectedClientId(newClientId);

    // For connect-instances routes, also update sessionStorage
    if (isConnectInstancesRoute) {
      if (newClientId) {
        sessionStorage.setItem('connectInstanceClientId', newClientId.toString());
      } else {
        sessionStorage.removeItem('connectInstanceClientId');
      }
    }
  };

  // Handle toggle changes
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    console.log('[TopBar] Toggle changed:', checked);
    handleViewModeChange(checked);
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
        width: '100%'
      }}
    >
      <Toolbar sx={{ 
        minHeight: 64,
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {/* Logo */}
          <Box sx={{ flexGrow: 0, mr: 2, display: 'flex', alignItems: 'center' }}>
            <img
              src={travelMindsLogo}
              alt="Travel Minds Logo"
              style={{ height: '40px', width: 'auto' }}
            />
            <Typography
              variant="h6"
              sx={{
                ml: 2,
                color: 'text.primary',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Marketing Portal
            </Typography>
          </Box>

          {/* Agency Selection */}
          {showAgencySelector && (
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="client-select-label">Agency</InputLabel>
              <Select
                labelId="client-select-label"
                id="client-select"
                value={clients.length > 0 && selectedClientId ? selectedClientId : ''}
                label="Agency"
                onChange={handleClientChange}
                sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { py: 1 }
                }}
              >
                <MenuItem value="" disabled>
                  Select Agency
                </MenuItem>
                {clients?.slice().sort((a, b) => a.agency_name.localeCompare(b.agency_name)).map((client: Client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.agency_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Login/Register Buttons or User Menu */}
          {!auth.isAuthenticated ? (
            <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                href="/login"
              >
                Sign In / Register
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <Box sx={{ textAlign: 'right', mr: 2 }}>
                <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
                  {userDisplayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {clients?.find(c => c.id === selectedClientId)?.agency_name} {selectedClientId && (isAdmin() ? '(Admin)' : `(${getRoleDisplayName(clientAssignments.find(a => a.client_id === selectedClientId)?.role)})`)}
                </Typography>
              </Box>
              <Avatar sx={{
                width: 32,
                height: 32,
                bgcolor: isCognitoAdmin() ? 'error.main' : 'primary.main'
              }}>
                {userInitial}
              </Avatar>
              <IconButton
                onClick={handleMenu}
                size="small"
                sx={{ ml: 1 }}
                aria-controls={Boolean(anchorEl) ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
              >
                <KeyboardArrowDown />
              </IconButton>
              <Menu
                id="account-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                  },
                }}
              >
                {/* View Mode Toggle for Cognito Admins */}
                {isCognitoAdmin() && (
                  <MenuItem>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={viewMode === 'admin'}
                          onChange={handleToggleChange}
                          color="error"
                          size="small"
                        />
                      }
                      label={viewMode === 'admin' ? 'Admin View' : 'User View'}
                      sx={{ mx: 1 }}
                    />
                  </MenuItem>
                )}
                {isCognitoAdmin() && <Divider />}
                <MenuItem onClick={handleLogout} disabled={isLoggingOut}>
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar; 
